import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate, useParams } from "react-router-dom"
import { isNativePlatform, registerPushDevice, pushPlatform, getCachedPushToken, unregisterPushDevice, addPushActionListener, mirrorAuthToPreferences, restoreAuthFromPreferences } from "@/lib/native"
import { Capacitor } from "@capacitor/core"
import { SplashScreen } from "@capacitor/splash-screen"
import { App as CapApp } from "@capacitor/app"
import { ErrorBoundary } from "./components/molecules/ErrorBoundary"
import HubApp from "./hub/HubApp"
import { RouteBoundary } from "./components/molecules/RouteBoundary"
import { PermissionGate } from "./components/molecules/PermissionGate"
const NotFoundPage = lazy(() => import("./pages/NotFoundPage").then(m => ({ default: m.NotFoundPage })))
import { SidebarLayout } from "./components/templates/SidebarLayout"
import { MobileLayout } from "./components/templates/MobileLayout"
import { KioskRouteSwitch } from "./components/templates/KioskRouteSwitch"
import { MobileComingSoon } from "./components/molecules/MobileComingSoon"
import { ToastProvider } from "./components/molecules/Toast"
import { BrandingProvider } from "./contexts/BrandingContext"
import { ProjectProvider } from "./contexts/ProjectContext"
import { ProjectsProvider } from "./contexts/ProjectsContext"
import { SearchModal } from "./components/organisms/SearchModal"
import { ImpersonationBanner } from "./components/molecules/ImpersonationBanner"
// TASK-DETAIL-PEEK-SURFACE-SEARCH — global peek for task/decision search hits
import { TaskDetailPeek } from "./design-system/patterns"
import { appTasks, syncAuxTokenFromStorage, setToken, setRefreshToken, getActiveToken, resumeStoredSession, SIGNED_IN_EVENT } from "./lib/api"
import { isOutsideSessionToken, CLIENT_PORTAL_HOME } from "./lib/outsideRole"
import { consumeOAuthCallbackURL } from "@/lib/oauth-applink"
const ProjectsPage = lazy(() => import("./pages/ProjectsPage"))
const ProjectDetailPageNew = lazy(() => import("./pages/ProjectDetailPageNew"))
const ProjectMemoryPage = lazy(() => import("./pages/ProjectMemoryPage"))
const ProjectMaterialsPipelinePage = lazy(() => import("./pages/ProjectMaterialsPipelinePage"))
const ProjectSchedulePage = lazy(() => import("./pages/ProjectSchedulePage"))
const CostPlusDashboard = lazy(() => import("./pages/CostPlusDashboard"))
// TasksPage (legacy 2614-line standalone) replaced by unified ProjectSchedulePage
// at /tasks. The file is kept on disk for reference but no longer routed.
const BoardViewPage = lazy(() => import("./pages/BoardViewPage"))
const CommsPage = lazy(() => import("./pages/CommsPage"))
const AgentEventsPage = lazy(() => import("./pages/AgentEventsPage"))
const SettingsPageNew = lazy(() => import("./pages/SettingsPageNew"))
const ContactsPage = lazy(() => import("./pages/ContactsPage"))
const ContactProfilePage = lazy(() => import("./pages/ContactProfilePage"))
const CostCataloguePage = lazy(() => import("./pages/CostCataloguePage"))
const RFIsPage = lazy(() => import("./pages/RFIsPage"))
const PurchaseOrdersPage = lazy(() => import("./pages/PurchaseOrdersPage"))
import LoginPage from "./pages/LoginPage"
const DebugSiwaPage = lazy(() => import("./pages/DebugSiwaPage"))
import VerifyEmailPage from "./pages/VerifyEmailPage"
import SignupPage from "./pages/SignupPage"
import ForgotPasswordPage from "./pages/ForgotPasswordPage"
import ResetPasswordPage from "./pages/ResetPasswordPage"
const TermsPage = lazy(() => import("./pages/TermsPage"))
const PrivacyPage = lazy(() => import("./pages/PrivacyPage"))
const BudgetPage = lazy(() => import("./pages/BudgetPage"))
const BudgetOverviewPage = lazy(() => import("./pages/BudgetOverviewPage"))
const ChangeOrdersPage = lazy(() => import("./pages/ChangeOrdersPage"))
const EstimatesPage = lazy(() => import("./pages/EstimatesPage"))
const MobileEstimatesPage = lazy(() => import("./pages/MobileEstimatesPage"))
const MobileProposalsPage = lazy(() => import("./pages/MobileProposalsPage"))
const MobileProposalDetailPage = lazy(() => import("./pages/MobileProposalDetailPage"))
const MobileChangeOrdersPage = lazy(() => import("./pages/MobileChangeOrdersPage"))
const MobileRFIsPage = lazy(() => import("./pages/MobileRFIsPage"))
const MobileSubmittalsPage = lazy(() => import("./pages/MobileSubmittalsPage"))
const EstimateEditorPage = lazy(() => import("./pages/EstimateEditorPage"))
const EstimateAssemblyEditorPage = lazy(() => import("./pages/EstimateAssemblyEditorPage"))
const CatalogAssemblyEditorPage = lazy(() => import("./pages/CatalogAssemblyEditorPage"))
const InvoicesPage = lazy(() => import("./pages/InvoicesPage"))
const DailyReportsPage = lazy(() => import("./pages/DailyReportsPage"))
const LeadsPage = lazy(() => import("./pages/LeadsPage"))
const LeadDetailPage = lazy(() => import("./pages/LeadDetailPage"))
const SelectionsPage = lazy(() => import("./pages/SelectionsPage"))
const ClientSelectionsPage = lazy(() => import("./pages/ClientSelectionsPage"))
const FinishSchedulePage = lazy(() => import("./pages/FinishSchedulePage"))
const FinishScheduleItemEditPage = lazy(() => import("./pages/FinishScheduleItemEditPage"))
const FinishSchedulePrintPage = lazy(() => import("./pages/FinishSchedulePrintPage"))
const ScheduleTaskListPrintPage = lazy(() => import("./pages/ScheduleTaskListPrintPage"))
const ProjectPreviewAsPage = lazy(() => import("./pages/ProjectPreviewAsPage"))
const FinishScheduleAllProjectsPage = lazy(() => import("./pages/FinishScheduleAllProjectsPage"))
const FinishScheduleTemplatesPage = lazy(() => import("./pages/FinishScheduleTemplatesPage"))
const SpecPage = lazy(() => import("./pages/SpecPage"))
const SelectionItemsPage = lazy(() => import("./pages/SelectionItemsPage"))
const TimeTrackingPage = lazy(() => import("./pages/TimeTrackingPage"))
const BillsPage = lazy(() => import("./pages/BillsPage"))
const ReimbursementsPage = lazy(() => import("./pages/ReimbursementsPage"))
const FilesPage = lazy(() => import("./pages/FilesPage"))
const PurchasingPage = lazy(() => import("./pages/PurchasingPage"))
const SchedulePage = lazy(() => import("./pages/SchedulePage"))
const MultiProjectGanttPage = lazy(() => import("./pages/MultiProjectGanttPage"))
const PublicGanttPage = lazy(() => import("./pages/PublicGanttPage"))
const MobileRoomSpecPage = lazy(() => import("./pages/MobileRoomSpecPage"))
const PortalPreviewPage = lazy(() => import("./pages/PortalPreviewPage"))
const PortalSummaryPage = lazy(() => import("./pages/PortalSummaryPage"))
const ClientPortalPageEnhanced = lazy(() => import("./pages/ClientPortalPageEnhanced"))
const ClientHomePortalPage = lazy(() => import("./pages/ClientHomePortalPage"))
const VendorPortalPage = lazy(() => import("./pages/VendorPortalPage"))
const SubmittalReviewPortalPage = lazy(() => import("./pages/SubmittalReviewPortalPage"))
const SubConfirmPortalPage = lazy(() => import("./pages/SubConfirmPortalPage"))
const PortalEstimatePage = lazy(() => import("./pages/PortalEstimatePage"))
const ClientLogSharePage = lazy(() => import("./pages/ClientLogSharePage"))
const PortalPreviewAuthPage = lazy(() => import("./pages/PortalPreviewAuthPage"))
const AdminDashboardPage = lazy(() => import("./pages/AdminDashboardPage"))
const ActivityPage = lazy(() => import("./pages/ActivityPage"))
const AdminTenantsPage = lazy(() => import("./pages/AdminTenantsPage"))
const AdminJurisdictionRequirements = lazy(() => import("./pages/AdminJurisdictionRequirements"))
const AdminJurisdictionPermitTypes = lazy(() => import("./pages/AdminJurisdictionPermitTypes"))
const AdminHandbookLibraryPage = lazy(() => import("./pages/AdminHandbookLibraryPage"))
const ClipperTelemetryPage = lazy(() => import("./pages/ClipperTelemetryPage"))
const DashboardEditPage = lazy(() => import("./pages/DashboardEditPage"))
const CompanyDashboardPage = lazy(() => import("./pages/CompanyDashboardPage"))
const DashboardKioskPage = lazy(() => import("./pages/DashboardKioskPage"))
const SignupsPage = lazy(() => import("./pages/SignupsPage"))
const SubmittalsPage = lazy(() => import("./pages/SubmittalsPage"))
const BidRequestsPage = lazy(() => import("./pages/BidRequestsPage"))
const TimesheetsPage = lazy(() => import("./pages/TimesheetsPage"))
const FinancePage = lazy(() => import("./pages/FinancePage"))
const ReportsPage = lazy(() => import('./pages/ReportsPage'))
const ProfitabilityPage = lazy(() => import('./pages/ProfitabilityPage'))
const MobileTasksPage = lazy(() => import("./pages/MobileTasksPage"))
const MobileContactsPage = lazy(() => import("./pages/MobileContactsPage"))
const MobileFilesPage = lazy(() => import("./pages/MobileFilesPage"))
const MobileEquipmentPage = lazy(() => import("./pages/MobileEquipmentPage"))
const MobileLocationsPage = lazy(() => import("./pages/MobileLocationsPage"))
const MobileSagePage = lazy(() => import("./pages/MobileSagePage"))
const MobileCreateTaskPage = lazy(() => import("./pages/MobileCreateTaskPage"))
const MobileTaskDetailPage = lazy(() => import("./pages/MobileTaskDetailPage"))
const MobileTimePage = lazy(() => import("./pages/MobileTimePage"))
const MobileDailyReportPage = lazy(() => import("./pages/MobileDailyReportPage"))
const MobileMessagesPage = lazy(() => import("./pages/MobileMessagesPage"))
const MobileTodayPage = lazy(() => import("./pages/MobileTodayPage"))
const MobileSchedulePage = lazy(() => import("./pages/MobileSchedulePage"))
const FieldMySchedulePage = lazy(() => import("./pages/FieldMySchedulePage"))
const MobileProjectSchedulePage = lazy(() => import("./pages/MobileProjectSchedulePage"))
const MobileProjectHubPage = lazy(() => import("./pages/MobileProjectHubPage"))
const MobileProjectPhotosPage = lazy(() => import("./pages/MobileProjectPhotosPage"))
const MobileProjectLogPage = lazy(() => import("./pages/MobileProjectLogPage"))
const MobileProjectProcurementPage = lazy(() => import("./pages/MobileProjectProcurementPage"))
const MobileProcurementPage = lazy(() => import("./pages/MobileProcurementPage"))
const MobileFinishScheduleIndexPage = lazy(() => import("./pages/MobileFinishScheduleIndexPage"))
const MobileFinishScheduleAllProjectsPage = lazy(() => import("./pages/MobileFinishScheduleAllProjectsPage"))
const MobileFinishScheduleRoomPage = lazy(() => import("./pages/MobileFinishScheduleRoomPage"))
const MobileProjectRFIsPage = lazy(() => import("./pages/MobileProjectRFIsPage"))
const MobileInvoicesPage = lazy(() => import("./pages/MobileInvoicesPage"))
const MobileInvoiceDetailPage = lazy(() => import("./pages/MobileInvoiceDetailPage"))
const MobileEstimateDetailPage = lazy(() => import("./pages/MobileEstimateDetailPage"))
const MobileBillsPage = lazy(() => import("./pages/MobileBillsPage"))
const PhotosPage = lazy(() => import("./pages/PhotosPage"))
const DrawingsPage = lazy(() => import("./pages/DrawingsPage"))
const PunchListsPage = lazy(() => import("./pages/PunchListsPage"))
const SageActivityPage = lazy(() => import("./pages/SageActivityPage"))
const SageInboxPage = lazy(() => import("./pages/SageInboxPage"))
const SageMemoryPage = lazy(() => import("./pages/SageMemoryPage"))
const HandbookPage = lazy(() => import("./pages/HandbookPage"))
const SafetyPage = lazy(() => import("./pages/SafetyPage"))
const WarrantyPage = lazy(() => import("./pages/WarrantyPage"))
const ComplianceCalendarPage = lazy(() => import("./pages/ComplianceCalendarPage"))
const CompliancePage = lazy(() => import("./pages/CompliancePage"))
// Sprint 16 - New pages
const EquipmentPage = lazy(() => import("./pages/EquipmentPage"))
const InternalPage = lazy(() => import("./pages/InternalPage"))
const VehiclesPage = lazy(() => import("./pages/VehiclesPage"))
const MaterialsPage = lazy(() => import("./pages/MaterialsPage"))
const SubcontractsV2Page = lazy(() => import("./pages/SubcontractsV2Page"))
const SubcontractorsPage = lazy(() => import("./pages/SubcontractorsPage"))
const ProposalsPage = lazy(() => import("./pages/ProposalsPage"))
const ProposalComposerPage = lazy(() => import("./pages/ProposalComposerPage"))
const InvoiceComposerPage = lazy(() => import("./pages/InvoiceComposerPage"))
const ProcurementComposerPage = lazy(() => import("./pages/ProcurementComposerPage"))
const ProposalPortalPage = lazy(() => import("./pages/ProposalPortalPage"))
const ProposalSignPage = lazy(() => import("./pages/ProposalSignPage"))
const InvoicePortalPage = lazy(() => import("./pages/InvoicePortalPage"))
const ChangeOrderComposerPage = lazy(() => import("./pages/ChangeOrderComposerPage"))
const ChangeOrderPortalPage = lazy(() => import("./pages/ChangeOrderPortalPage"))
const QuickEstimatePage = lazy(() => import("./pages/QuickEstimatePage"))
const AssembliesPage = lazy(() => import("./pages/AssembliesPage"))
const ActivityLogPage = lazy(() => import("./pages/ActivityLogPage"))
const ProfilePage = lazy(() => import("./pages/ProfilePage"))
const NotificationsPage = lazy(() => import("./pages/NotificationsPage"))
const SearchResultsPage = lazy(() => import("./pages/SearchResultsPage"))
const ResourcesPage = lazy(() => import("./pages/ResourcesPage"))
const SourcingPage = lazy(() => import("./pages/SourcingPage"))
const ProcurementHubPage = lazy(() => import("./pages/ProcurementHubPage"))
// Sprint 17 - Daily Logs, Document Templates, Field UI
const DailyLogsPage = lazy(() => import("./pages/DailyLogsPage"))
const DailyLogDetailPage = lazy(() => import("./pages/DailyLogDetailPage"))
const ProjectLogsPage = lazy(() => import("./pages/ProjectLogsPage"))
const ProjectTeamPage = lazy(() => import("./pages/ProjectTeamPage"))
const DocumentTemplatesPage = lazy(() => import("./pages/DocumentTemplatesPage"))
const ProjectTemplatesPage = lazy(() => import("./pages/ProjectTemplatesPage"))
// Sprint 25 - Design Approvals
const DesignApprovalsPage = lazy(() => import("./pages/DesignApprovalsPage"))
const EquipmentDetailPage = lazy(() => import("./pages/EquipmentDetailPage"))
const ShopOrdersPage = lazy(() => import("./pages/ShopOrdersPage"))
const ShopDesignPage = lazy(() => import("./pages/ShopDesignPage"))
const FieldPage = lazy(() => import("./pages/FieldPage"))
// Sprint 38 - Planned Maintenance + Equipment Identification
const MaintenancePage = lazy(() => import("./pages/MaintenancePage"))
const PermitsPage = lazy(() => import("./pages/PermitsPage"))
const TakeoffsPage = lazy(() => import("./pages/TakeoffsPage"))
const TakeoffCanvasPage = lazy(() => import("./pages/TakeoffCanvasPage"))
const TakeoffComparePage = lazy(() => import("./pages/TakeoffComparePage"))
const TakeoffBidPacketPrintPage = lazy(() => import("./pages/TakeoffBidPacketPrintPage"))
const PermitDetailPage = lazy(() => import("./pages/PermitDetailPage").then(m => ({ default: m.PermitDetailPage })))
const NewPermitFlowPage = lazy(() => import("./pages/NewPermitFlowPage"))
const HomeMaintenancePage = lazy(() => import("./pages/HomeMaintenancePage"))
const CallbacksPage = lazy(() => import("./pages/CallbacksPage"))
const HomeServicePage = lazy(() => import("./pages/HomeServicePage"))
const DecisionsPage = lazy(() => import("./pages/DecisionsPage"))
const DecisionsReportPage = lazy(() => import("./pages/DecisionsReportPage"))
const DecisionDetailPage = lazy(() => import("./pages/DecisionDetailPage"))
const GoalsPage = lazy(() => import("./pages/GoalsPage"))
const ClientUpdatesPage = lazy(() => import("./pages/ClientUpdatesPage"))
const ConstraintsPage = lazy(() => import("./pages/ConstraintsPage"))
const ShopTasksPage = lazy(() => import("./pages/ShopTasksPage"))
const LocationKioskPage = lazy(() => import("./pages/LocationKioskPage"))
const KioskBigBoardPage = lazy(() => import("./pages/KioskBigBoardPage"))
const LocationsPage = lazy(() => import("./pages/LocationsPage"))
const LocationDetailPage = lazy(() => import("./pages/LocationDetailPage"))
import { useEffect, useState, useCallback, Suspense } from "react"
// Route chunks use lazyWithRetry (not React.lazy) so a deploy-window transient
// (server restart gap / renamed chunk) retries across ~5s and loads seamlessly
// instead of stranding the tab with "Importing a module script failed".
import { lazyWithRetry as lazy } from "@/lib/lazyWithRetry"
import { sseManager } from "./hooks/useSSE"
import i18n from "./i18n"
import { MobileClipSheet } from "./components/organisms/MobileClipSheet"

// Sprint 28
const BillingPage = lazy(() => import('./pages/BillingPage'))
const BillingAlertsPage = lazy(() => import('./pages/BillingAlertsPage'))
const InternalBillingPage = lazy(() => import('./pages/InternalBillingPage'))
const InternalOverviewPage = lazy(() => import('./pages/InternalOverviewPage'))
const InternalTasksPage = lazy(() => import('./pages/InternalTasksPage'))
const InternalEquipmentPage = lazy(() => import('./pages/InternalEquipmentPage'))
const OverheadPage = lazy(() => import('./pages/OverheadPage'))
const OnboardingPage = lazy(() => import('./pages/OnboardingPage'))
const ScanPage = lazy(() => import('./pages/ScanPage'))
const AcceptInvitePage = lazy(() => import('./pages/AcceptInvitePage'))
const ContactInviteAcceptPage = lazy(() => import('./pages/ContactInviteAcceptPage'))
const ChangelogPage = lazy(() => import('./pages/ChangelogPage'))
const DesktopTimerPopoverPage = lazy(() => import('./pages/DesktopTimerPopoverPage'))
const SettingsRolesPage = lazy(() => import('./pages/SettingsRolesPage'))
const RoleEditorPage = lazy(() => import('./pages/settings/roles/RoleEditor'))
const ClipperAuthPage = lazy(() => import('./pages/ClipperAuthPage'))
const ClipperTokensPage = lazy(() => import('./pages/settings/ClipperTokensPage'))
const MobileSettingsPage = lazy(() => import('./pages/MobileSettingsPage'))
const MobileSettingsRolesPage = lazy(() => import('./pages/MobileSettingsRolesPage'))
const MobileSettingsBrandingPage = lazy(() => import('./pages/MobileSettingsBrandingPage'))
const MobileCompanySettingsPage = lazy(() => import('./pages/MobileCompanySettingsPage'))
const MobileCompanySettingsShellPage = lazy(() => import('./pages/MobileCompanySettingsShellPage'))
const MobileSettingsIntegrationsPage = lazy(() => import('./pages/MobileSettingsIntegrationsBillingActivityPages').then(m => ({ default: m.MobileSettingsIntegrationsPage })))
const MobileSettingsBillingPage = lazy(() => import('./pages/MobileSettingsIntegrationsBillingActivityPages').then(m => ({ default: m.MobileSettingsBillingPage })))
const MobileSettingsActivityPage = lazy(() => import('./pages/MobileSettingsIntegrationsBillingActivityPages').then(m => ({ default: m.MobileSettingsActivityPage })))
const MobileSettingsNotificationsPage = lazy(() => import('./pages/MobileSettingsNotificationsPage'))
const MobileWorkdaySettingsPage = lazy(() => import('./pages/MobileWorkdaySettingsPage'))
const MobileSettingsDesktopTabPage = lazy(() => import('./pages/MobileSettingsDesktopTabPage'))
const MobileProfilePage = lazy(() => import('./pages/MobileProfilePage'))
const MobilePurchaseOrdersPage = lazy(() => import('./pages/MobilePurchaseOrdersPage'))
const MobileInspectionFlagsPage = lazy(() => import("./pages/MobileInspectionFlagsPage"))
const SuperAgentTemplatesPage = lazy(() => import('./pages/SuperAgentTemplatesPage'))
const DesignSystemSpecimenPage = lazy(() => import('./pages/DesignSystemSpecimenPage').then(m => ({ default: m.DesignSystemSpecimenPage })))
// HUB-12-13b — god-view + super_admin-only usage pages retired from the
// customer SPA. All platform-admin surfaces live on hub.usetenon.com now.
const MessagesPage = lazy(() => import('./pages/MessagesPage'))
// Sprint 22 - Team Chat
const TeamChatPage = lazy(() => import('./pages/TeamChatPage'))
const PunchListPage = lazy(() => import('./pages/PunchListPage').then(m => ({ default: m.PunchListPage })))
const ConsumableInventoryPage = lazy(() => import('./pages/ConsumableInventoryPage'))
import { usePullToRefresh } from '@/hooks/usePullToRefresh'
import { useDocumentTitle } from '@/hooks/useDocumentTitle'
import { UpdateBanner } from '@/components/atoms/UpdateBanner'
import { PageSpinner } from '@/components/atoms/Spinner'
import { useTaskPeekDependencies } from '@/hooks/useTaskPeekDependencies'

// SSO token handoff — must run SYNCHRONOUSLY at module load, before React
// renders Routes. Google/Apple OAuth callback redirects to
// `/#google_token=<JWT>&next=<path>`; if we wait for a useEffect to stash
// the token, <RequireAuth> sees no token during the first render and
// Navigate→/login fires before the effect runs. Result: an apparent login
// loop. Doing this at module load means localStorage is populated before
// React boots, so RequireAuth sees the token on the first check.
//
// AUTH-02b: the backend carries these in the URL FRAGMENT, not the query
// string (internal/api/google_oauth.go, apple_oauth.go). Fragments are never
// sent to the server — no access-log/CDN-log exposure — and are stripped
// from the Referer header, unlike query strings.
;(function consumeSSOTokenFromURL() {
  if (typeof window === 'undefined') return
  const params = new URLSearchParams(window.location.hash.replace(/^#/, ''))
  const ssoToken = params.get('google_token') || params.get('apple_token')
  if (!ssoToken) return
  // Not awaited: this IIFE must finish the ml_token write SYNCHRONOUSLY
  // (see comment above) before React's first render, but setToken's
  // localStorage writes (token + decoded ml_token_exp) already run
  // synchronously before its first await — the Watch sync / native mirror /
  // SSE side effects that follow are fine to finish on a later microtask.
  // A raw setItem here left ml_token_exp stale/absent, the same sub-defect
  // #2091 fixed for impersonation (#2141).
  setToken(ssoToken)
  // #645: OAuth callbacks now also return a rotating refresh token so the
  // 15-min access token refreshes silently instead of forcing a daily re-login.
  // #2172 BUG 4: route it through setRefreshToken() (mirrors to Preferences /
  // biometric storage) instead of a raw localStorage write, same as the token
  // above — a direct setItem here left the native/biometric mirror stale on a
  // fresh web SSO login.
  const ssoRefresh = params.get('refresh_token')
  if (ssoRefresh) setRefreshToken(ssoRefresh)
  const next = params.get('next') || '/projects'
  window.history.replaceState({}, '', next)
})()

// Watch sync — re-push the existing token to the paired Apple Watch on every
// app boot AND on every app-becomes-active. Joe-flagged 2026-05-21 (build 27):
// Watch app stuck on "Waiting for iPhone to sync…" because
// _syncWatchToken in api.ts only fires inside setToken() / clearToken() —
// i.e. only on login/logout. When the iPhone app boots with a token already
// in localStorage (TestFlight update, app restart), the native bridge's
// in-memory lastToken stays empty, so Watch's whatsMyToken reply is empty.
// Re-push on boot fixes that. Re-push on resume fixes the Watch-opened-after-
// phone-was-backgrounded case.
;(function bootstrapWatchSync() {
  if (typeof window === 'undefined') return
  const w = window
  const isNative = !!w?.Capacitor?.isNativePlatform?.()
  if (!isNative) return
  const pushToken = () => {
    const tok = localStorage.getItem('ml_token')
    if (!tok) return
    // Avoid coupling to api.ts internals — registerPlugin is the public Capacitor API.
    import('@capacitor/core').then(({ registerPlugin }) => {
      const WatchAuth = registerPlugin('WatchAuth')
      const voice = localStorage.getItem('sage_voice_pref_v1') || 'af_heart'
      WatchAuth.syncToken({ token: tok, baseUrl: 'https://app.usetenon.com', voice }).catch(() => {})
    }).catch(() => {})
  }
  // Initial push: token already in localStorage from prior session
  setTimeout(pushToken, 500) // small delay so Capacitor bridge has activated
  // Re-push on every app-becomes-active so a Watch wake-up after iPhone was
  // backgrounded picks up the fresh token without requiring the user to
  // re-launch the iPhone app.
  import('@capacitor/app').then(({ App }) => {
    App.addListener('appStateChange', ({ isActive }) => {
      if (isActive) pushToken()
    })
  }).catch(() => {})
})()

/** Tiny component that re-applies document.title on every route change.
 *  Must render inside BrowserRouter (uses useLocation). Renders nothing. */
function DocumentTitleSetter({ suffix = '' }) {
  useDocumentTitle(suffix)
  return null
}

function RequireAuth({ children }) {
  const location = useLocation()
  const token = getActiveToken()  // #2151: preview tab resolves its own sessionStorage token
  if (!token) {
    return <Navigate to={`/login?next=${encodeURIComponent(location.pathname + location.search)}`} replace />
  }
  // #3963: outside users (clients etc.) never mount a staff page — they land
  // in the dedicated client portal. The server refuses them staff routes too (#3961).
  if (isOutsideSessionToken(token)) {
    return <Navigate to={CLIENT_PORTAL_HOME} replace />
  }
  return children
}

// #3963: the staff shell (sidebar, search, Sage, SSE) must not mount for an
// outside user at all — gate it before AppLayout runs any of its effects.
function StaffShellGate({ children }) {
  if (isOutsideSessionToken(getActiveToken())) {
    return <Navigate to={CLIENT_PORTAL_HOME} replace />
  }
  return children
}


// OnboardingGuard: after login, check if onboarding is needed
function OnboardingGuard({ children }) {
  const location = useLocation()
  const navigate = useNavigate()
  const [checked, setChecked] = useState(false)
  const [needsOnboarding, setNeedsOnboarding] = useState(false)

  useEffect(() => {
    // Skip for /onboarding path itself
    if (location.pathname === '/onboarding') {
      setChecked(true)
      return
    }
    // Check token claims
    const token = getActiveToken()  // #2151
    if (!token) { setChecked(true); return }
    try {
      const claims = JSON.parse(atob(token.split('.')[1]))
      // Skip for admin
      if (claims.email === 'admin@meadowlark.dev') { setChecked(true); return }
    } catch { setChecked(true); return }
    // Check cached flag
    const done = localStorage.getItem('ml_onboarding_complete')
    if (done === 'true') { setChecked(true); return }
    // Fetch from API
    const token2 = getActiveToken()  // #2151
    fetch((window?.Capacitor?.isNativePlatform?.() ? 'https://app.usetenon.com' : '') + '/api/v1/onboarding', {
      headers: { Authorization: 'Bearer ' + token2 }
    }).then(r => r.json()).then(data => {
      if (!data.onboarding_complete) {
        setNeedsOnboarding(true)
      } else {
        localStorage.setItem('ml_onboarding_complete', 'true')
      }
      setChecked(true)
    }).catch(() => setChecked(true))
  }, [location.pathname])

  if (!checked) return null
  if (needsOnboarding) return <Navigate to="/onboarding" replace />
  return children
}

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(isNativePlatform() || window.innerWidth < 768)
  useEffect(() => {
    if (isNativePlatform()) return  // native: always mobile, skip listener
    const handler = () => setIsMobile(window.innerWidth < 768)
    window.addEventListener('resize', handler)
    return () => window.removeEventListener('resize', handler)
  }, [])
  return isMobile
}

function MobileRoutes() {
  const token = getActiveToken()  // #2151: preview tab renders as the client, not the admin
  const user = (() => { try { return JSON.parse(atob(token.split('.')[1])) } catch { return {} } })()

  return (
    <ProjectProvider>
    <ProjectsProvider>
    <RequireAuth>
      <MobileLayout userName={user.name || "Tenon"}>
        <Suspense fallback={<PageSpinner />}>
        <Routes>
          {/* Phase B P-01: Today is tab 1. */}
          <Route path="/m/today" element={<RouteBoundary><MobileTodayPage /></RouteBoundary>} />
          {/* Phase B §05: project hub replaces the legacy tap-project→tasks redirect. */}
          <Route path="/m/p/:id" element={<RouteBoundary><MobileProjectHubPage /></RouteBoundary>} />
          {/* Phase C §07.1: project-context module sub-routes. Existing pages already read
              :id from useParams and use it as the project filter. */}
          <Route path="/m/p/:id/tasks" element={<RouteBoundary><MobileTasksPage /></RouteBoundary>} />
          <Route path="/m/p/:id/rfi" element={<RouteBoundary><MobileProjectRFIsPage /></RouteBoundary>} />
          <Route path="/m/p/:id/co" element={<RouteBoundary><MobileChangeOrdersPage /></RouteBoundary>} />
          <Route path="/m/p/:id/schedule" element={<RouteBoundary><MobileProjectSchedulePage /></RouteBoundary>} />
          <Route path="/m/p/:id/procurement" element={<RouteBoundary><MobileProjectProcurementPage /></RouteBoundary>} />
          <Route path="/m/p/:id/log" element={<RouteBoundary><MobileProjectLogPage /></RouteBoundary>} />
          <Route path="/m/p/:id/photos" element={<RouteBoundary><MobileProjectPhotosPage /></RouteBoundary>} />
          <Route path="/m/finish-schedule" element={<RouteBoundary><MobileFinishScheduleAllProjectsPage /></RouteBoundary>} />
          <Route path="/m/p/:id/fs" element={<RouteBoundary><MobileFinishScheduleIndexPage /></RouteBoundary>} />
          <Route path="/m/p/:id/fs/:roomFolderId" element={<RouteBoundary><MobileFinishScheduleRoomPage /></RouteBoundary>} />
          {/* Phase C §07.4: Sage destination route — FAB lights up via pathname. */}
          <Route path="/m/sage" element={<RouteBoundary><MobileSagePage /></RouteBoundary>} />
          <Route path="/m/tasks" element={<RouteBoundary><MobileTasksPage /></RouteBoundary>} />
          <Route path="/m/tasks/new" element={<RouteBoundary><MobileCreateTaskPage /></RouteBoundary>} />
          <Route path="/m/tasks/:id" element={<RouteBoundary><MobileTaskDetailPage /></RouteBoundary>} />
          <Route path="/m/time" element={<RouteBoundary><MobileTimePage /></RouteBoundary>} />
          <Route path="/m/reports" element={<RouteBoundary><MobileDailyReportPage /></RouteBoundary>} />
          <Route path="/m/messages" element={<RouteBoundary><MobileMessagesPage /></RouteBoundary>} />
          {/* More drawer routes */}
          <Route path="/m/invoices" element={<RouteBoundary><MobileInvoicesPage /></RouteBoundary>} />
          <Route path="/m/invoices/:id" element={<RouteBoundary><MobileInvoiceDetailPage /></RouteBoundary>} />
          <Route path="/m/estimates" element={<RouteBoundary><MobileEstimatesPage /></RouteBoundary>} />
          <Route path="/m/estimates/:id" element={<RouteBoundary><MobileEstimateDetailPage /></RouteBoundary>} />
          <Route path="/m/proposals" element={<RouteBoundary><MobileProposalsPage /></RouteBoundary>} />
          <Route path="/m/proposals/:id" element={<RouteBoundary><MobileProposalDetailPage /></RouteBoundary>} />
          <Route path="/m/change-orders" element={<RouteBoundary><MobileChangeOrdersPage /></RouteBoundary>} />
          <Route path="/m/purchase-orders" element={<RouteBoundary><MobilePurchaseOrdersPage /></RouteBoundary>} />
          <Route path="/m/bills" element={<RouteBoundary><MobileBillsPage /></RouteBoundary>} />
          <Route path="/m/bills/new" element={<RouteBoundary><MobileBillsPage /></RouteBoundary>} />
          <Route path="/m/rfis" element={<RouteBoundary><MobileRFIsPage /></RouteBoundary>} />
          <Route path="/m/submittals" element={<RouteBoundary><MobileSubmittalsPage /></RouteBoundary>} />
          <Route path="/m/procurement" element={<RouteBoundary><MobileProcurementPage /></RouteBoundary>} />
          <Route path="/m/p/:id/permits/:permitId/inspections/:inspectionId" element={<RouteBoundary><MobileInspectionFlagsPage /></RouteBoundary>} />
          <Route path="/m/files" element={<RouteBoundary><MobileFilesPage /></RouteBoundary>} />
          {/* Spec P-04: Nothing silently routes to /m/tasks. Show ComingSoon. */}
          <Route path="/m/my-schedule" element={<RouteBoundary><FieldMySchedulePage /></RouteBoundary>} />
          <Route path="/m/schedule" element={<RouteBoundary><MobileSchedulePage /></RouteBoundary>} />
          <Route path="/m/timesheets" element={<RouteBoundary><MobileTimePage /></RouteBoundary>} />
          <Route path="/m/settings" element={<RouteBoundary><MobileSettingsPage /></RouteBoundary>} />
          <Route path="/m/settings/roles" element={<RouteBoundary><MobileSettingsRolesPage /></RouteBoundary>} />
          <Route path="/m/settings/branding" element={<RouteBoundary><MobileSettingsBrandingPage /></RouteBoundary>} />
          <Route path="/m/settings/company" element={<RouteBoundary><MobileCompanySettingsPage /></RouteBoundary>} />
          <Route path="/m/settings/company-settings" element={<RouteBoundary><MobileCompanySettingsShellPage /></RouteBoundary>} />
          <Route path="/m/settings/integrations" element={<RouteBoundary><MobileSettingsIntegrationsPage /></RouteBoundary>} />
          <Route path="/m/settings/billing" element={<RouteBoundary><MobileSettingsBillingPage /></RouteBoundary>} />
          <Route path="/m/settings/activity" element={<RouteBoundary><MobileSettingsActivityPage /></RouteBoundary>} />
          <Route path="/m/settings/notifications" element={<RouteBoundary><MobileSettingsNotificationsPage /></RouteBoundary>} />
          <Route path="/m/settings/workday" element={<RouteBoundary><MobileWorkdaySettingsPage /></RouteBoundary>} />
          <Route path="/m/settings/desktop-tab/:tabKey" element={<RouteBoundary><MobileSettingsDesktopTabPage /></RouteBoundary>} />
          <Route path="/m/profile" element={<RouteBoundary><MobileProfilePage /></RouteBoundary>} />
          <Route path="/m/equipment" element={<RouteBoundary><MobileEquipmentPage /></RouteBoundary>} />
          {/* /:id catches both /new (id='new' → create form) and real IDs.
              A separate literal /new route used to live here but React Router
              picked the literal over :id, leaving useParams().id undefined
              and falling through to the list view. Add button looked dead. */}
          <Route path="/m/equipment/:id" element={<RouteBoundary><MobileEquipmentPage /></RouteBoundary>} />
          <Route path="/m/locations" element={<RouteBoundary><MobileLocationsPage /></RouteBoundary>} />
          <Route path="/m/contacts" element={<RouteBoundary><MobileContactsPage /></RouteBoundary>} />
          <Route path="*" element={<RouteBoundary><MobileComingSoon /></RouteBoundary>} />
        </Routes>
        </Suspense>
      </MobileLayout>
    </RequireAuth>
    </ProjectsProvider>
    </ProjectProvider>
  )
}

function DesktopRoutes({ onOpenSearch }) {
  const location = useLocation()
  const token = getActiveToken()  // #2151: preview tab renders as the client, not the admin
  const user = (() => { try { return JSON.parse(atob(token.split('.')[1])) } catch { return {} } })()
  return (
    <ProjectProvider>
    <ProjectsProvider>
    <RequireAuth><OnboardingGuard>
      <SidebarLayout 
        currentPath={location.pathname} 
        userName={user.name || "Tenon"} 
        userRole={user.role || "admin"}
        onOpenSearch={onOpenSearch}
      >
        <Suspense fallback={<PageSpinner />}>
        <Routes>
          <Route path="/dashboard" element={<Navigate to="/dashboards" replace />} />
          <Route path="/projects" element={<RouteBoundary><ProjectsPage /></RouteBoundary>} />
          <Route path="/projects/:id/:section?" element={<RouteBoundary><ProjectDetailPageNew /></RouteBoundary>} />
          <Route path="/contacts" element={<RouteBoundary><ContactsPage /></RouteBoundary>} />
          <Route path="/contacts/:id/profile" element={<RouteBoundary><ContactProfilePage /></RouteBoundary>} />
          <Route path="/contacts/:id" element={<RouteBoundary><ContactProfilePage /></RouteBoundary>} />
          <Route path="/cost-catalogue" element={<RouteBoundary><CostCataloguePage /></RouteBoundary>} />
          {/* Legacy URL for anyone bookmarked on the old settings tab */}
          <Route path="/settings/cost-catalog" element={<Navigate to="/cost-catalogue" replace />} />
          {/* Unified Tasks hub: same component as project schedule, no projectId = global mode */}
          <Route path="/tasks" element={<RouteBoundary><ProjectSchedulePage /></RouteBoundary>} />
          <Route path="/tasks/board" element={<Navigate to="/tasks?view=board" replace />} />
          <Route path="/tasks/gantt" element={<Navigate to="/tasks?view=gantt" replace />} />
          <Route path="/tasks/calendar" element={<Navigate to="/tasks?view=calendar" replace />} />
          {/* Legacy /board route — points at projects board, kept for now */}
          <Route path="/board" element={<RouteBoundary><BoardViewPage /></RouteBoundary>} />
          <Route path="/rfis" element={<RouteBoundary><RFIsPage /></RouteBoundary>} />
          <Route path="/team" element={<Navigate to="/contacts?tab=users" replace />} />
          <Route path="/purchase-orders" element={<Navigate to="/procurement?tab=purchase-orders" replace />} />
          <Route path="/comms" element={<RouteBoundary><CommsPage /></RouteBoundary>} />
          <Route path="/agent-events" element={<RouteBoundary><AgentEventsPage /></RouteBoundary>} />
          <Route path="/settings" element={<RouteBoundary><SettingsPageNew /></RouteBoundary>} />
          <Route path="/settings/roles" element={<RouteBoundary><SettingsRolesPage /></RouteBoundary>} />
          <Route path="/settings/roles/:roleId" element={<RouteBoundary><RoleEditorPage /></RouteBoundary>} />
          <Route path="/settings/finish-schedule-templates" element={<RouteBoundary><FinishScheduleTemplatesPage /></RouteBoundary>} />
          <Route path="/clipper-auth" element={<RouteBoundary><ClipperAuthPage /></RouteBoundary>} />
          <Route path="/settings/clipper-tokens" element={<RouteBoundary><ClipperTokensPage /></RouteBoundary>} />
          <Route path="/admin/analytics" element={<RouteBoundary><AdminDashboardPage /></RouteBoundary>} />
          <Route path="/admin/activity" element={<RouteBoundary><ActivityLogPage /></RouteBoundary>} />
          <Route path="/admin/tenants" element={<RouteBoundary><AdminTenantsPage /></RouteBoundary>} />
          <Route path="/admin/tenants/:id" element={<RouteBoundary><AdminTenantsPage /></RouteBoundary>} />
          <Route path="/admin/signups" element={<RouteBoundary><SignupsPage /></RouteBoundary>} />
          <Route path="/admin/jurisdiction-requirements" element={<RouteBoundary><AdminJurisdictionRequirements /></RouteBoundary>} />
          <Route path="/admin/jurisdiction-permit-types" element={<RouteBoundary><AdminJurisdictionPermitTypes /></RouteBoundary>} />
          <Route path="/admin/handbook-library" element={<RouteBoundary><AdminHandbookLibraryPage /></RouteBoundary>} />
          <Route path="/admin/clipper-health" element={<RouteBoundary><ClipperTelemetryPage /></RouteBoundary>} />
          <Route path="/dashboards" element={<RouteBoundary><CompanyDashboardPage /></RouteBoundary>} />
          <Route path="/dashboards/:id" element={<RouteBoundary><DashboardEditPage /></RouteBoundary>} />
          <Route path="/profile" element={<RouteBoundary><ProfilePage /></RouteBoundary>} />
          <Route path="/notifications" element={<RouteBoundary><NotificationsPage /></RouteBoundary>} />
          <Route path="/search" element={<RouteBoundary><SearchResultsPage /></RouteBoundary>} />
          <Route path="/budget" element={<RouteBoundary><BudgetPage /></RouteBoundary>} />
          <Route path="/budget/overview" element={<RouteBoundary><BudgetOverviewPage /></RouteBoundary>} />
          <Route path="/budget-overview" element={<RouteBoundary><BudgetOverviewPage /></RouteBoundary>} />
          <Route path="/change-orders" element={<RouteBoundary><ChangeOrdersPage /></RouteBoundary>} />
          <Route path="/change-orders/:coId/edit" element={<RouteBoundary><ChangeOrderComposerPage /></RouteBoundary>} />
          <Route path="/estimates" element={<RouteBoundary><EstimatesPage /></RouteBoundary>} />
          <Route path="/estimates/:id" element={<RouteBoundary><EstimateEditorPage /></RouteBoundary>} />
          <Route path="/estimates/:estimateId/assemblies/:assemblyId" element={<RouteBoundary><EstimateAssemblyEditorPage /></RouteBoundary>} />
          <Route path="/cost-catalogue/assemblies/:assemblyId" element={<RouteBoundary><CatalogAssemblyEditorPage /></RouteBoundary>} />
          <Route path="/invoices" element={<RouteBoundary><InvoicesPage /></RouteBoundary>} />
          <Route path="/invoices/:invoiceId/edit" element={<RouteBoundary><InvoiceComposerPage /></RouteBoundary>} />
          <Route path="/subcontracts" element={<Navigate to="/procurement?tab=subcontracts" replace />} />
          <Route path="/subcontractors" element={<RouteBoundary><SubcontractorsPage /></RouteBoundary>} />
          <Route path="/daily-reports" element={<RouteBoundary><DailyReportsPage /></RouteBoundary>} />
          <Route path="/daily-logs" element={<RouteBoundary><DailyLogsPage /></RouteBoundary>} />
          <Route path="/leads" element={<RouteBoundary><PermissionGate resource="leads"><LeadsPage /></PermissionGate></RouteBoundary>} />
          <Route path="/leads/:id" element={<RouteBoundary><PermissionGate resource="leads"><LeadDetailPage /></PermissionGate></RouteBoundary>} />
          <Route path="/selections" element={<Navigate to="/decisions" replace />} />
          <Route path="/projects/:id/selections" element={<Navigate to="/decisions" replace />} />
          <Route path="/projects/:id/client-selections" element={<RouteBoundary><ClientSelectionsPage /></RouteBoundary>} />
          <Route path="/finish-schedule" element={<RouteBoundary><FinishScheduleAllProjectsPage /></RouteBoundary>} />
          <Route path="/projects/:id/finish-schedule" element={<RouteBoundary><FinishSchedulePage /></RouteBoundary>} />
          {/* Full-page edit/add — Joe directive 2026-05-22: no SlideOver, no peek */}
          <Route path="/projects/:id/finish-schedule/new" element={<RouteBoundary><FinishScheduleItemEditPage /></RouteBoundary>} />
          <Route path="/projects/:id/finish-schedule/:itemId/edit" element={<RouteBoundary><FinishScheduleItemEditPage /></RouteBoundary>} />
          <Route path="/projects/:id/finish-schedule/print" element={<RouteBoundary><FinishSchedulePrintPage /></RouteBoundary>} />
          <Route path="/projects/:id/schedule/print" element={<RouteBoundary><ScheduleTaskListPrintPage /></RouteBoundary>} />
          {/* GC preview-as-role — mints an ephemeral 5-min portal token via
              POST /api/v1/projects/{id}/preview-as/{role} and redirects to
              the matching client / collaborator / trade view. Lives inside
              AppLayout so GC session auth is available. */}
          <Route path="/projects/:id/preview-as/:role" element={<RouteBoundary><ProjectPreviewAsPage /></RouteBoundary>} />
          <Route path="/projects/:id/spec" element={<RouteBoundary><SpecPage /></RouteBoundary>} />
          <Route path="/projects/:id/memory" element={<RouteBoundary><ProjectMemoryPage /></RouteBoundary>} />
          <Route path="/projects/:id/selection-items" element={<RouteBoundary><SelectionItemsPage /></RouteBoundary>} />
          <Route path="/time-tracking" element={<RouteBoundary><TimeTrackingPage /></RouteBoundary>} />
          <Route path="/schedule" element={<RouteBoundary><SchedulePage /></RouteBoundary>} />
          <Route path="/gantt" element={<RouteBoundary><MultiProjectGanttPage /></RouteBoundary>} />
          <Route path="/resources" element={<RouteBoundary><ResourcesPage /></RouteBoundary>} />
          <Route path="/sourcing" element={<RouteBoundary><SourcingPage /></RouteBoundary>} />
          {/* Bills + Expenses live under Procurement (all-projects + per-project). Redirect legacy URLs. */}
          <Route path="/bills" element={<Navigate to="/procurement?tab=bills" replace />} />
          <Route path="/expenses/reimbursements" element={<RouteBoundary><ReimbursementsPage /></RouteBoundary>} />
          <Route path="/files" element={<RouteBoundary><FilesPage /></RouteBoundary>} />
          <Route path="/expenses" element={<Navigate to="/procurement?tab=expenses" replace />} />
          <Route path="/purchasing" element={<Navigate to="/procurement?tab=shipments" replace />} />
          <Route path="/submittals" element={<RouteBoundary><SubmittalsPage /></RouteBoundary>} />
          <Route path="/bid-requests" element={<Navigate to="/procurement?tab=bid-requests" replace />} />
          <Route path="/projects/:id/bid-requests" element={<Navigate to="/procurement?tab=bid-requests" replace />} />
          <Route path="/projects/:id/procurement" element={<RouteBoundary><ProcurementHubPage /></RouteBoundary>} />
          <Route path="/procurement-chain" element={<Navigate to="/procurement" replace />} />
          <Route path="/procurement" element={<RouteBoundary><ProcurementHubPage /></RouteBoundary>} />
          <Route path="/timesheets" element={<RouteBoundary><TimesheetsPage /></RouteBoundary>} />
          <Route path="/finance" element={<RouteBoundary><FinancePage /></RouteBoundary>} />
          <Route path="/reports" element={<RouteBoundary><ReportsPage /></RouteBoundary>} />
          <Route path="/reports/profitability" element={<RouteBoundary><ProfitabilityPage /></RouteBoundary>} />
          <Route path="/messages" element={<RouteBoundary><TeamChatPage /></RouteBoundary>} />
          <Route path="/messages/inbox" element={<Navigate to="/messages" replace />} />
          <Route path="/chat" element={<Navigate to="/messages" replace />} />
          <Route path="/projects/:projectId/photos" element={<RouteBoundary><PhotosPage /></RouteBoundary>} />
          <Route path="/projects/:projectId/drawings" element={<RouteBoundary><DrawingsPage /></RouteBoundary>} />
          {/* Takeoffs (plural) — T-P3 list pages live now; canvas page is a
              placeholder pending the same row's continued WIP (pdf.js +
              Konva surface). */}
          <Route path="/takeoffs" element={<RouteBoundary><PermissionGate resource="takeoffs"><TakeoffsPage /></PermissionGate></RouteBoundary>} />
          <Route path="/projects/:id/takeoffs" element={<RouteBoundary><PermissionGate resource="takeoffs"><TakeoffsPage /></PermissionGate></RouteBoundary>} />
          <Route path="/projects/:id/takeoffs/:takeoffId" element={<RouteBoundary><TakeoffCanvasPage /></RouteBoundary>} />
          <Route path="/projects/:id/takeoffs/:takeoffId/compare/:otherDocId" element={<RouteBoundary><TakeoffComparePage /></RouteBoundary>} />
          <Route path="/takeoff-sheet-sets/:setId/bid-packet" element={<RouteBoundary><TakeoffBidPacketPrintPage /></RouteBoundary>} />
          <Route path="/projects/:projectId/punch-lists" element={<RouteBoundary><PunchListsPage /></RouteBoundary>} />
          <Route path="/sage/activity" element={<RouteBoundary><SageActivityPage /></RouteBoundary>} />
          <Route path="/sage/inbox" element={<RouteBoundary><SageInboxPage /></RouteBoundary>} />
          <Route path="/sage/memory" element={<RouteBoundary><SageMemoryPage /></RouteBoundary>} />
          <Route path="/handbook" element={<RouteBoundary><HandbookPage /></RouteBoundary>} />
          <Route path="/projects/:id/punch-list" element={<RouteBoundary><PunchListPage /></RouteBoundary>} />
          <Route path="/design-approvals" element={<Navigate to="/decisions" replace />} />
          <Route path="/projects/:id/designs" element={<RouteBoundary><DesignApprovalsPage /></RouteBoundary>} />
          <Route path="/safety" element={<RouteBoundary><SafetyPage /></RouteBoundary>} />
          <Route path="/warranties" element={<RouteBoundary><WarrantyPage /></RouteBoundary>} />
          <Route path="/activity" element={<RouteBoundary><ActivityPage /></RouteBoundary>} />
          <Route path="/compliance" element={<RouteBoundary><CompliancePage /></RouteBoundary>} />
          <Route path="/compliance/calendar" element={<RouteBoundary><ComplianceCalendarPage /></RouteBoundary>} />
          {/* Sprint 16 - New routes */}
          <Route path="/equipment" element={<RouteBoundary><EquipmentPage /></RouteBoundary>} />
          {/* Internal Section — sub-routes */}
          <Route path="/internal" element={<RouteBoundary><InternalOverviewPage /></RouteBoundary>} />
          <Route path="/internal/tasks" element={<RouteBoundary><InternalTasksPage /></RouteBoundary>} />
          <Route path="/internal/timesheets" element={<RouteBoundary><TimesheetsPage /></RouteBoundary>} />
          <Route path="/internal/equipment" element={<Navigate to="/equipment" replace />} />
          <Route path="/internal/billing" element={<RouteBoundary><InternalPage /></RouteBoundary>} />
          <Route path="/internal/files" element={<RouteBoundary><FilesPage /></RouteBoundary>} />
          <Route path="/locations" element={<RouteBoundary><LocationsPage /></RouteBoundary>} />
          <Route path="/locations/:id" element={<RouteBoundary><LocationDetailPage /></RouteBoundary>} />
          <Route path="/consumables" element={<RouteBoundary><ConsumableInventoryPage /></RouteBoundary>} />
          <Route path="/equipment/:id" element={<RouteBoundary><EquipmentDetailPage /></RouteBoundary>} />
          <Route path="/maintenance" element={<RouteBoundary><MaintenancePage /></RouteBoundary>} />
          <Route path="/permits" element={<RouteBoundary><PermitsPage /></RouteBoundary>} />
          <Route path="/permits/new" element={<RouteBoundary><NewPermitFlowPage /></RouteBoundary>} />
          <Route path="/permits/:permitId" element={<RouteBoundary><PermitDetailPage /></RouteBoundary>} />
          <Route path="/home-maintenance" element={<RouteBoundary><HomeMaintenancePage /></RouteBoundary>} />
          <Route path="/callbacks" element={<RouteBoundary><CallbacksPage /></RouteBoundary>} />
          <Route path="/home-service" element={<RouteBoundary><HomeServicePage /></RouteBoundary>} />
          <Route path="/decisions" element={<RouteBoundary><DecisionsPage /></RouteBoundary>} />
          <Route path="/decisions/report" element={<RouteBoundary><DecisionsReportPage /></RouteBoundary>} />
          <Route path="/goals" element={<RouteBoundary><GoalsPage /></RouteBoundary>} />
          <Route path="/client-updates" element={<RouteBoundary><ClientUpdatesPage /></RouteBoundary>} />
          <Route path="/constraints" element={<RouteBoundary><ConstraintsPage /></RouteBoundary>} />
          {/* Sprint 29 - Shop Production Suite */}
          <Route path="/shop-orders" element={<RouteBoundary><ShopOrdersPage /></RouteBoundary>} />
          <Route path="/shop-design" element={<RouteBoundary><ShopDesignPage /></RouteBoundary>} />
          {/* Sprint 26 - Work Orders */}
          <Route path="/vehicles" element={<RouteBoundary><VehiclesPage /></RouteBoundary>} />
          <Route path="/materials" element={<RouteBoundary><MaterialsPage /></RouteBoundary>} />
          <Route path="/proposals" element={<RouteBoundary><ProposalsPage /></RouteBoundary>} />
          <Route path="/proposals/:proposalId" element={<RouteBoundary><ProposalComposerPage /></RouteBoundary>} />
          <Route path="/quick-estimate" element={<RouteBoundary><QuickEstimatePage /></RouteBoundary>} />
          <Route path="/billing" element={<RouteBoundary><BillingPage /></RouteBoundary>} />
          <Route path="/settings/billing" element={<RouteBoundary><BillingPage /></RouteBoundary>} />
          <Route path="/settings/billing-alerts" element={<RouteBoundary><BillingAlertsPage /></RouteBoundary>} />
          {/* /internal-billing → migrated to /internal/billing */}
          <Route path="/assemblies" element={<RouteBoundary><AssembliesPage /></RouteBoundary>} />
          {/* Sprint 33 - Project-scoped routes */}
          <Route path="/projects/:id/tasks" element={<RouteBoundary><TasksPageRedirect /></RouteBoundary>} />
          <Route path="/projects/:id/task-schedule" element={<RouteBoundary><ProjectSchedulePage /></RouteBoundary>} />
          <Route path="/projects/:id/rfis" element={<RouteBoundary><RFIsPage /></RouteBoundary>} />
          <Route path="/projects/:id/materials-pipeline" element={<RouteBoundary><ProjectMaterialsPipelinePage /></RouteBoundary>} />
          <Route path="/projects/:id/submittals" element={<RouteBoundary><SubmittalsPage /></RouteBoundary>} />
          <Route path="/projects/:id/daily-reports" element={<RouteBoundary><DailyReportsPage /></RouteBoundary>} />
          <Route path="/projects/:id/daily-logs" element={<RouteBoundary><DailyLogsPage /></RouteBoundary>} />
          <Route path="/projects/:id/bills" element={<Navigate to="../procurement?tab=bills" replace relative="path" />} />
          <Route path="/projects/:id/expenses" element={<Navigate to="../procurement?tab=expenses" replace relative="path" />} />
          <Route path="/projects/:id/purchase-orders" element={<RouteBoundary><PurchaseOrdersPage /></RouteBoundary>} />
          {/* #606 Stage 2 — Unified procurement composer */}
          <Route path="/projects/:projectId/purchase-orders/:docId/compose" element={<RouteBoundary><ProcurementComposerPage docKind="purchase_order" /></RouteBoundary>} />
          <Route path="/projects/:projectId/bid-requests/:docId/compose" element={<RouteBoundary><ProcurementComposerPage docKind="bid_request" /></RouteBoundary>} />
          <Route path="/projects/:projectId/subcontracts/:docId/compose" element={<RouteBoundary><ProcurementComposerPage docKind="subcontract" /></RouteBoundary>} />
          <Route path="/projects/:id/timesheets" element={<RouteBoundary><TimesheetsPage /></RouteBoundary>} />
          <Route path="/projects/:id/contacts" element={<RouteBoundary><ContactsPage /></RouteBoundary>} />
          <Route path="/projects/:id/comms" element={<RouteBoundary><CommsPage /></RouteBoundary>} />
          <Route path="/projects/:id/change-orders" element={<RouteBoundary><ChangeOrdersPage /></RouteBoundary>} />
          <Route path="/projects/:id/invoices" element={<RouteBoundary><InvoicesPage /></RouteBoundary>} />
          <Route path="/projects/:id/cost-plus" element={<RouteBoundary><CostPlusDashboard /></RouteBoundary>} />
          {/* Sprint 17 - Document Templates */}
          <Route path="/document-templates" element={<RouteBoundary><DocumentTemplatesPage /></RouteBoundary>} />
          {/* PT1 - Project Templates */}
          <Route path="/project-templates" element={<RouteBoundary><ProjectTemplatesPage /></RouteBoundary>} />
          {/* Global routes for new sidebar items */}
          <Route path="/project-logs" element={<RouteBoundary><ProjectLogsPage /></RouteBoundary>} />
          <Route path="/overhead" element={<RouteBoundary><OverheadPage /></RouteBoundary>} />
          {/* Two-mode sidebar: project-scoped stub routes */}
          <Route path="/projects/:id/profitability" element={<RouteBoundary><ProfitabilityPage /></RouteBoundary>} />
          <Route path="/projects/:id/estimate" element={<RouteBoundary><EstimatesPage /></RouteBoundary>} />
          <Route path="/projects/:id/proposal" element={<RouteBoundary><ProposalsPage /></RouteBoundary>} />
          <Route path="/projects/:id/schedule" element={<RouteBoundary><ProjectSchedulePage /></RouteBoundary>} />
          <Route path="/projects/:id/project-logs" element={<RouteBoundary><ProjectLogsPage /></RouteBoundary>} />
          <Route path="/projects/:id/logs/:logId" element={<RouteBoundary><DailyLogDetailPage /></RouteBoundary>} />
          <Route path="/logs/:logId" element={<RouteBoundary><DailyLogDetailPage /></RouteBoundary>} />
          {/* Decisions → Finish Schedule replacement (brief May 2026: FS is the new GC-facing surface
              for the unified decisions primitive; project-scoped /decisions hard-redirects to /finish-schedule).
              Org-level /decisions stays as cross-project rollup. */}
          <Route path="/projects/:id/decisions" element={<DecisionsToFinishScheduleRedirect />} />
          <Route path="/projects/:id/decisions/report" element={<RouteBoundary><DecisionsReportPage /></RouteBoundary>} />
          <Route path="/projects/:id/decisions/:decisionId" element={<RouteBoundary><DecisionDetailPage /></RouteBoundary>} />
          <Route path="/projects/:id/permits" element={<RouteBoundary><PermitsPage /></RouteBoundary>} />
          <Route path="/projects/:id/permits/new" element={<RouteBoundary><NewPermitFlowPage /></RouteBoundary>} />
          <Route path="/projects/:id/permits/:permitId" element={<RouteBoundary><PermitDetailPage /></RouteBoundary>} />
          <Route path="/projects/:id/budget" element={<RouteBoundary><BudgetPage /></RouteBoundary>} />
          <Route path="/projects/:id/safety" element={<RouteBoundary><SafetyPage /></RouteBoundary>} />
          <Route path="/projects/:id/callbacks" element={<RouteBoundary><CallbacksPage /></RouteBoundary>} />
          <Route path="/projects/:id/home-service" element={<RouteBoundary><HomeServicePage /></RouteBoundary>} />
          <Route path="/projects/:id/consumables" element={<RouteBoundary><ConsumableInventoryPage /></RouteBoundary>} />
          <Route path="/projects/:id/files" element={<RouteBoundary><FilesPage /></RouteBoundary>} />
          <Route path="/projects/:id/overview" element={<RouteBoundary><ProjectDetailPageNew /></RouteBoundary>} />
          <Route path="/projects/:id/issues" element={<RouteBoundary><ConstraintsPage /></RouteBoundary>} />
          <Route path="/projects/:id/team" element={<RouteBoundary><ProjectTeamPage /></RouteBoundary>} />
          <Route path="*" element={<RouteBoundary><NotFoundPage /></RouteBoundary>} />
        </Routes>
        </Suspense>
      </SidebarLayout>
    </OnboardingGuard></RequireAuth>
    </ProjectsProvider>
    </ProjectProvider>
  )
}

// Redirect /projects/:id/tasks → /projects/:id/schedule?view=list
function TasksPageRedirect() {
  const { id } = useParams()
  return <Navigate to={`/projects/${id}/schedule?view=list`} replace />
}

// Redirect /projects/:id/decisions → /projects/:id/finish-schedule (Finish Schedule brief May 2026 §2)
function DecisionsToFinishScheduleRedirect() {
  const { id } = useParams()
  return <Navigate to={`/projects/${id}/finish-schedule`} replace />
}

function AppLayout() {
  const isMobile = useIsMobile()
  const location = useLocation()
  const navigate = useNavigate()
  const [searchOpen, setSearchOpen] = useState(false)
  const [gPressed, setGPressed] = useState(false)
  // TASK-DETAIL-PEEK-SURFACE-SEARCH — global peek for task/decision search hits
  const [searchPeekId, setSearchPeekId] = useState(null)
  const [searchPeekKind, setSearchPeekKind] = useState('task')
  const [searchPeekData, setSearchPeekData] = useState(null)
  const [searchPeekLoading, setSearchPeekLoading] = useState(false)
  // SCHED-W4 §13 — stacked-peek back-stack for dependency-row navigation, plus
  // this task's own dependencies (predecessors) + blocking (successors).
  const [searchPeekStack, setSearchPeekStack] = useState([])

  // Global keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Ignore if typing in an input
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA' || e.target.isContentEditable) {
        return
      }

      // Cmd/Ctrl + K -> Search
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault()
        setSearchOpen(true)
        return
      }

      // Escape -> Close modal
      if (e.key === 'Escape') {
        setSearchOpen(false)
        return
      }

      // G then letter navigation
      if (e.key === 'g') {
        setGPressed(true)
        setTimeout(() => setGPressed(false), 500)
        return
      }

      if (gPressed) {
        switch (e.key) {
          case 'p': navigate('/projects'); break
          case 'i': navigate('/invoices'); break
          case 't': navigate('/tasks'); break
          case 'c': navigate('/contacts'); break
          case 'h': navigate('/'); break
          case 'e': navigate('/equipment'); break
          case 'm': navigate('/materials'); break
          case 'f': navigate('/field'); break
          case 'd': navigate('/daily-logs'); break
        }
        setGPressed(false)
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [gPressed, navigate])

  // TASK-DETAIL-PEEK-SURFACE-SEARCH — fetch task/decision data when a search result opens the peek
  useEffect(() => {
    if (!searchPeekId) { setSearchPeekData(null); return }
    setSearchPeekLoading(true)
    appTasks.get(searchPeekId)
      .then(t => setSearchPeekData(t))
      .catch(() => {})
      .finally(() => setSearchPeekLoading(false))
  }, [searchPeekId])

  // Load the open task's dependencies (predecessors) + blocking (successors),
  // scoped by its project — shared across every peek-opening surface (see
  // useTaskPeekDependencies).
  const { deps: searchPeekDeps, blocking: searchPeekBlocking, removeDependency: removeSearchPeekDependency } = useTaskPeekDependencies(searchPeekData)

  // Open a dependency/blocking task in a stacked peek — pushes the currently-open
  // item's {id, kind, name} so Back can restore it.
  const handleOpenSearchPeekTask = useCallback((id) => {
    if (searchPeekId && searchPeekData) {
      setSearchPeekStack(prev => [...prev, {
        id: searchPeekId,
        kind: searchPeekData.schedule_type ?? searchPeekKind,
        name: searchPeekData.name,
      }])
    }
    setSearchPeekId(id)
  }, [searchPeekId, searchPeekData, searchPeekKind])

  const handleSearchPeekBack = useCallback(() => {
    setSearchPeekStack(prev => {
      if (prev.length === 0) return prev
      const top = prev[prev.length - 1]
      setSearchPeekId(top.id)
      setSearchPeekKind(top.kind)
      return prev.slice(0, -1)
    })
  }, [])

  if (location.pathname.startsWith('/m/')) {
    return <MobileRoutes />
  }

  if (isMobile &&
      !location.pathname.startsWith("/m/") &&
      !location.pathname.startsWith("/desktop/") &&
      !location.pathname.startsWith("/portal") &&
      !location.pathname.startsWith("/kiosk") &&
      !location.pathname.startsWith("/onboarding") &&
      !location.pathname.startsWith("/login") &&
      !location.pathname.startsWith("/signup") &&
      !location.pathname.startsWith("/forgot") &&
      !location.pathname.startsWith("/reset") &&
      !location.pathname.startsWith("/invite") &&
      !location.pathname.startsWith("/changelog") &&
      !location.pathname.startsWith("/accept")) {
    return <Navigate to="/m/today" replace />
  }

  return (
    <>
      <ImpersonationBanner />
      <DesktopRoutes onOpenSearch={() => setSearchOpen(true)} />
      <SearchModal
        isOpen={searchOpen}
        onClose={() => setSearchOpen(false)}
        onOpenPeek={(id, kind) => { setSearchPeekId(id); setSearchPeekKind(kind) }}
      />
      {/* TASK-DETAIL-PEEK-SURFACE-SEARCH — global peek opened by search results */}
      <TaskDetailPeek
        open={!!searchPeekId}
        kind={searchPeekData?.schedule_type ?? searchPeekKind}
        task={searchPeekData && !searchPeekLoading ? {
          id:        searchPeekData.id,
          name:      searchPeekData.name,
          kind:      searchPeekData.schedule_type ?? searchPeekKind,
          status:    searchPeekData.status,
          priority:  searchPeekData.priority,
          assignees: searchPeekData.assignee_names ?? (searchPeekData.assignee_name ? [searchPeekData.assignee_name] : []),
          project:   searchPeekData.project_name ?? null,
          notes:     searchPeekData.notes,
          start:     searchPeekData.start_date ?? null,
          due:       searchPeekData.due_date ?? null,
          dependencies: searchPeekDeps,
        } : null}
        onClose={() => { setSearchPeekId(null); setSearchPeekData(null); setSearchPeekStack([]) }}
        onOpenTask={handleOpenSearchPeekTask}
        onBack={searchPeekStack.length > 0 ? handleSearchPeekBack : undefined}
        breadcrumb={searchPeekStack.length > 0 ? searchPeekStack[searchPeekStack.length - 1].name : undefined}
        blockingTasks={searchPeekBlocking}
        onRemoveDependency={removeSearchPeekDependency}
        canEdit={false}
        taskTypeEnabled={false}
        phases={[]}
      />
    </>
  )
}

// Hub vs app dispatch — the SAME SPA bundle is served on both
// app.usetenon.com and hub.usetenon.com. Host check at the top of App() mounts
// the hub-only HubApp on hub.* hostnames; everything below is the standard app.
const IS_HUB_HOST = typeof window !== 'undefined' && /^hub\./.test(window.location.hostname)

export default function App() {
  if (IS_HUB_HOST) {
    return (
      <ErrorBoundary>
        <HubApp />
      </ErrorBoundary>
    )
  }

  usePullToRefresh()

  // Register service worker for browser push notifications
  // Silent: no permission prompt. Users opt in via Notification Settings.
  useEffect(() => {
    if ("serviceWorker" in navigator && "PushManager" in window) {
      navigator.serviceWorker.register("/sw.js").catch(() => {})
    }
  }, [])

  // Hide native splash screen once React has mounted
  useEffect(() => {
    if (Capacitor.isNativePlatform()) {
      SplashScreen.hide({ fadeOutDuration: 300 }).catch(() => {})
    }
  }, [])

  // Handle OAuth deep-link / App Link callbacks:
  //   com.usetenon.app://auth?code=...                         — success (PKCE, #728)
  //   https://app.usetenon.com/auth/callback?code=...          — success (Android App Link, AUTH-02c #1520)
  //   com.usetenon.app://auth?error=...                        — failure (surface to user, don't strand)
  //                                                              incl. error=upgrade_required for pre-code-flow
  //                                                              builds (legacy raw-token redirect retired, #1520 Part 2)
  //   com.usetenon.app://signup?method=apple&email=...&name=...
  //                                                             — first-time SIWA, route to signup
  // App Store rejection 2026-05-21 (build 28) root cause: server was returning
  // web /signup URLs on first-time SIWA. SFSafariViewController rendered the
  // page but the parent app never got a token. Server now deep-links instead;
  // this handler catches the deep link and routes to the right surface.
  //
  // #2172 (AUTH-02c regression): this app-root listener is the only one
  // that's ALWAYS mounted — LoginPage's own listener only exists while
  // /login is on screen. Before this fix it bailed on anything but the
  // custom scheme, so it silently ignored the verified HTTPS App Link
  // Android now uses. Combined with a cold start (Android evicts the app
  // under memory pressure while the OAuth Custom Tab is foreground,
  // reopening it via the App Link before any listener can attach, and
  // Capacitor does not replay a missed cold-start appUrlOpen), the login
  // silently dropped: app opened logged-out, code never consumed, no error
  // shown. Fixed by (a) recognizing the App Link + running the shared PKCE
  // exchange here too (consumeOAuthCallbackURL, shared with LoginPage.tsx so
  // the logic and the single-use-code dedup guard can't diverge), and
  // (b) draining App.getLaunchUrl() below to catch a callback that arrived
  // before this listener was attached.
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return

    const handleCallback = async (url) => {
      try {
        const result = await consumeOAuthCallbackURL(url)
        if (result.kind === 'auth-success') {
          window.location.href = result.next
        } else if (result.kind === 'auth-error') {
          // Surface the error to LoginPage via query string so the user sees
          // a real message instead of a silent failure.
          window.location.href = '/login?error=' + encodeURIComponent(result.errCode)
        } else if (result.kind === 'no-account') {
          // App Store Guideline 4 + 3.1.1 + "no mobile signup" directive: a
          // first-time SSO user on native must NEVER reach the /signup
          // password form. Account creation is web-only.
          window.location.href = '/login?sso_no_account=1'
        }
      } catch {}
    }

    const handle = CapApp.addListener('appUrlOpen', ({ url }) => { handleCallback(url) })

    // Cold-start recovery: if Android evicted the app while the OAuth Custom
    // Tab was foreground, the App Link relaunches the app before this
    // listener attaches, and Capacitor does not replay the missed
    // appUrlOpen. getLaunchUrl() is the only way to recover it.
    CapApp.getLaunchUrl().then((launch) => {
      if (launch?.url) handleCallback(launch.url)
    }).catch(() => {})

    return () => { handle.then(h => h.remove()) }
  }, [])

  // Handle iOS Share Extension deep-link: tenon://clip?url=<encoded-product-url>
  const [mobileClipUrl, setMobileClipUrl] = useState(null)
  useEffect(() => {
    if (!isNativePlatform()) return
    const handle = CapApp.addListener('appUrlOpen', ({ url }) => {
      try {
        const parsed = new URL(url)
        if (parsed.protocol === 'tenon:' && parsed.host === 'clip') {
          const productUrl = parsed.searchParams.get('url')
          if (productUrl) {
            setMobileClipUrl(decodeURIComponent(productUrl))
          }
        }
      } catch {}
    })
    return () => { handle.then(h => h.remove()) }
  }, [])

  // Re-push the stored auth token into UserDefaults.standard (read by CarPlay +
  // Apple Watch) on boot and on every resume. Without this, an already-signed-in
  // user who updates the app never re-syncs — CarPlay shows empty Today/Projects/
  // Recent and Sage says "Sign in required" even though the phone is signed in.
  useEffect(() => {
    if (!isNativePlatform()) return
    syncAuxTokenFromStorage()
    const handle = CapApp.addListener('appStateChange', async ({ isActive }) => {
      if (isActive) {
        // Resuming: WKWebView can drop localStorage while backgrounded (not
        // just across app updates), which silently deletes the refresh token
        // mid-session and forces a fresh login on the next 401 (#1096
        // AUTH-MOBILE-01). Refill from durable Preferences BEFORE the app's
        // first post-resume request — restoreAuthFromPreferences only fills
        // keys that are empty, so this is a no-op when localStorage survived.
        await restoreAuthFromPreferences()
        syncAuxTokenFromStorage()
        // Proactively top up an expired access token on resume (#3106). After a
        // long background the 15-min access token is expired; without this the
        // first post-resume request 401s and — if the native app can't read the
        // WWW-Authenticate refresh signal — logs the user out instead of
        // refreshing. No-op when the access token is still live.
        try { await resumeStoredSession() } catch { /* transient — next request retries */ }
      } else {
        // Backgrounding: iOS applies app updates while the app is in the
        // background, and can wipe WKWebView localStorage. Persist the latest
        // access/refresh/trusted-device tokens into durable Preferences NOW so
        // the post-update restore has the CURRENT (not a stale) refresh token —
        // a stale one trips reuse-detection and forces a full re-login + 2FA.
        await mirrorAuthToPreferences()
      }
    })
    return () => { handle.then(h => h.remove()) }
  }, [])

  // Connect SSE — singleton manager, safe to call multiple times
  useEffect(() => {
    const token = getActiveToken()  // #2151: preview tab's own SSE uses its sessionStorage token
    if (token) sseManager.connect(token)
    // Re-connect when storage changes (login from another tab, token refresh)
    const onStorage = (e) => {
      if (e.key === 'ml_token' && e.newValue) sseManager.connect(e.newValue)
      if (e.key === 'ml_token' && !e.newValue) sseManager.disconnect()
    }
    window.addEventListener('storage', onStorage)
    return () => window.removeEventListener('storage', onStorage)
  }, [])

  // Mobile M2 push primitive — register the device with APNs/FCM and persist
  // the token in `device_tokens` so the future dispatcher (M2-Push-2) can fan
  // out. Cached-token path keeps subsequent app launches from re-prompting.
  // Web bundle is a no-op (registerPushDevice returns null off-platform).
  useEffect(() => {
    let cancelled = false
    let inFlight = false
    const run = async () => {
      if (!isNativePlatform() || inFlight) return
      if (!localStorage.getItem('ml_token')) return
      const platform = pushPlatform()
      if (!platform) return
      inFlight = true
      try {
        let pushToken = await getCachedPushToken()
        if (!pushToken) {
          pushToken = await registerPushDevice()
        }
        if (cancelled || !pushToken) return
        // Read the token at POST time, not at run() start: registration can
        // take seconds, and the session may have refreshed meanwhile.
        const token = localStorage.getItem('ml_token')
        if (!token) return
        await fetch((window?.Capacitor?.isNativePlatform?.() ? 'https://app.usetenon.com' : '') + '/api/v1/users/me/devices', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + token },
          body: JSON.stringify({ platform, token: pushToken }),
        })
      } catch {} finally {
        inFlight = false
      }
    }
    run()
    // Re-run on sign-in in THIS webview (setToken fires SIGNED_IN_EVENT when the
    // user changes) — the `storage` event below only fires for other tabs, so on
    // its own a fresh sign-in never registered until the next cold launch.
    const onSignedIn = () => { run() }
    const onStorage = (e) => { if (e.key === 'ml_token' && e.newValue) run() }
    window.addEventListener(SIGNED_IN_EVENT, onSignedIn)
    window.addEventListener('storage', onStorage)
    return () => {
      cancelled = true
      window.removeEventListener(SIGNED_IN_EVENT, onSignedIn)
      window.removeEventListener('storage', onStorage)
    }
  }, [])

  // Phase C §07.3 — push tap-to-open deep link.
  // When the user taps a notification in the OS notification center, the
  // payload's `data` field carries either an explicit `link` (already-baked
  // route) OR an `entity_type` + `entity_id` pair that we resolve to a
  // mobile route here. Server-side payload shape is gated on the M2-Push-2
  // dispatcher (Joe-side APNs/FCM creds); this handler is forward-
  // compatible — extra payload fields are ignored.
  useEffect(() => {
    if (!isNativePlatform()) return
    let unsubscribe = () => {}
    addPushActionListener((action) => {
      const data = (action && action.notification && action.notification.data) || {}
      let path = null
      if (typeof data.link === 'string' && data.link.startsWith('/')) {
        path = data.link
      } else if (data.entity_type && data.entity_id) {
        switch (data.entity_type) {
          case 'rfi':
            path = data.project_id ? `/m/p/${data.project_id}/rfi` : '/m/rfis'
            break
          case 'change_order':
            path = data.project_id ? `/m/p/${data.project_id}/co` : '/m/change-orders'
            break
          case 'task':
            path = '/m/tasks'
            break
          case 'daily_report':
            path = data.project_id ? `/m/p/${data.project_id}/log` : '/m/reports'
            break
          default:
            path = null
        }
      }
      if (path) {
        // BrowserRouter navigate isn't accessible from here at component
        // mount — use the platform back-channel: change location.hash so
        // the route updates after the panel hydrates. For BrowserRouter
        // (no hash routing), pushState + popstate dispatch is the standard
        // pattern; React Router subscribes to popstate.
        window.history.pushState({}, '', path)
        window.dispatchEvent(new PopStateEvent('popstate'))
      }
    }).then((u) => { unsubscribe = u })
    return () => unsubscribe()
  }, [])

  // Mobile M2 — revoke the device's push token on sign-out so we don't keep
  // sending pushes addressed to the prior user. Listens for ml_token cleared
  // via storage event (other tab) AND wraps unregisterPushDevice locally.
  useEffect(() => {
    const onStorage = async (e) => {
      if (e.key !== 'ml_token' || e.newValue) return
      if (!isNativePlatform()) return
      const cachedTok = await getCachedPushToken()
      if (cachedTok) {
        try {
          await fetch((window?.Capacitor?.isNativePlatform?.() ? 'https://app.usetenon.com' : '') + '/api/v1/users/me/devices?token=' + encodeURIComponent(cachedTok), {
            method: 'DELETE',
            headers: { 'Content-Type': 'application/json' },
            keepalive: true,
          })
        } catch {}
      }
      await unregisterPushDevice()
    }
    window.addEventListener('storage', onStorage)
    return () => window.removeEventListener('storage', onStorage)
  }, [])

  // Apply DB-backed language preference on app load
  useEffect(() => {
    const token = localStorage.getItem('ml_token')
    if (!token) {
      // No user — use browser language as default
      const browserLang = navigator.language.slice(0, 2)
      if (['en', 'fr', 'es', 'de', 'pt'].includes(browserLang)) {
        i18n.changeLanguage(browserLang)
      }
      return
    }
    fetch((window?.Capacitor?.isNativePlatform?.() ? 'https://app.usetenon.com' : '') + '/api/v1/profile', { headers: { Authorization: 'Bearer ' + token } })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (data && data.language_preference) {
          i18n.changeLanguage(data.language_preference)
          localStorage.setItem('ml_lang', data.language_preference)
        } else {
          // Fall back to browser language
          const browserLang = navigator.language.slice(0, 2)
          const supported = ['en', 'fr', 'es', 'de', 'pt']
          const lang = supported.includes(browserLang) ? browserLang : 'en'
          i18n.changeLanguage(lang)
        }
      })
      .catch(() => {})
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Kiosk token passthrough — if ?kiosk= is in URL, store as ml_token
  // This allows /shop-tasks and /equipment to auth API calls without a user login
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const kioskToken = params.get('kiosk')
    if (kioskToken && !localStorage.getItem('ml_token')) {
      // skipSideEffects (#2141): shared kiosk device — must not reach the
      // paired Apple Watch or the durable native mirror of whoever else uses
      // this device. ml_token_exp is still set (not skipped by setToken).
      setToken(kioskToken, { skipSideEffects: true })
    }
  }, [])

  // SSO token handoff for Google/Apple now runs SYNCHRONOUSLY at module
  // load (top of this file). Doing it in a useEffect raced <RequireAuth>
  // and caused an apparent login loop — token stashed too late, Navigate
  // to /login already fired during the first render.

  return (
    <ErrorBoundary>
      <UpdateBanner />
    <BrowserRouter>
      <DocumentTitleSetter />
      <BrandingProvider>
        <ToastProvider>
          <Suspense fallback={<PageSpinner />}>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/onboarding" element={<OnboardingPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/portal" element={<ClientHomePortalPage />} />
            <Route path="/portal/preview" element={<PortalPreviewPage />} />
            <Route path="/portal/preview-auth" element={<PortalPreviewAuthPage />} />
            <Route path="/portal/:token/summary" element={<PortalSummaryPage />} />
            <Route path="/portal/:token" element={<ClientPortalPageEnhanced />} />
            <Route path="/portal/proposal/:token" element={<ProposalPortalPage />} />
            <Route path="/portal/proposal/by-id/:publicId" element={<ProposalPortalPage />} />
            <Route path="/proposal-sign/:token" element={<ProposalSignPage />} />
            <Route path="/portal/invoice/:invoiceId" element={<InvoicePortalPage />} />
            <Route path="/portal/invoice/public/:token" element={<InvoicePortalPage />} />
            <Route path="/portal/change-order/:token" element={<ChangeOrderPortalPage />} />
            <Route path="/portal/vendor/:token" element={<VendorPortalPage />} />
            <Route path="/portal/review/:token" element={<SubmittalReviewPortalPage />} />
            <Route path="/portal/confirm/:token" element={<SubConfirmPortalPage />} />
            <Route path="/client/portal/:token/estimates/:id" element={<PortalEstimatePage />} />
            <Route path="/log-share/:token" element={<ClientLogSharePage />} />
            <Route path="/field" element={<RequireAuth><FieldPage /></RequireAuth>} />
            <Route path="/shop-tasks" element={<ShopTasksPage />} />
            <Route path="/locations/:id/kiosk" element={<ProjectsProvider><KioskRouteSwitch BigBoardPage={KioskBigBoardPage} LegacyPage={LocationKioskPage} /></ProjectsProvider>} />
            <Route path="/dashboards/:id/kiosk" element={<DashboardKioskPage />} />
            <Route path="/public/gantt/:token" element={<PublicGanttPage />} />
            <Route path="/room-spec/:token/:locationId" element={<MobileRoomSpecPage />} />
            <Route path="/" element={<Navigate to={getActiveToken() ? '/dashboard' : '/login'} replace />} />
            <Route path="/super/agent-templates" element={<SuperAgentTemplatesPage />} />
            <Route path="/signup" element={<SignupPage />} />
            <Route path="/debug/siwa" element={<DebugSiwaPage />} />
              <Route path="/verify-email" element={<VerifyEmailPage />} />
            <Route path="/accept-invite" element={<AcceptInvitePage />} />
            <Route path="/invite/:token" element={<ContactInviteAcceptPage />} />
            <Route path="/changelog" element={<ChangelogPage />} />
            <Route path="/scan/:code" element={<ScanPage />} />
            {/* Bug #76: popover was outside ProjectsProvider → project dropdown empty.
                Wrap in ProjectsProvider so useProjects() sees the loaded list.
                RequireAuth is belt-and-suspenders (the page already guards itself). */}
            <Route path="/desktop/timer-popover" element={
              <ProjectsProvider>
                <RequireAuth>
                  <DesktopTimerPopoverPage />
                </RequireAuth>
              </ProjectsProvider>
            } />
            <Route path="/super/ds-specimen" element={<DesignSystemSpecimenPage />} />
            {/*
              Finish-schedule print route is hoisted OUT of AppLayout so the
              spec sheet PDF doesn't render with sidebar / search / FAB chrome
              from the standard app shell. The page still RequireAuths via
              its own apiGet (401 → /login redirect). Match the chromedp URL
              shape exactly (id can be UUID or numeric).
            */}
            <Route path="/projects/:id/finish-schedule/print" element={<RequireAuth><FinishSchedulePrintPage /></RequireAuth>} />
            <Route path="/projects/:id/schedule/print" element={<RequireAuth><ScheduleTaskListPrintPage /></RequireAuth>} />
            <Route path="/*" element={<StaffShellGate><AppLayout /></StaffShellGate>} />
          </Routes>
          </Suspense>
        </ToastProvider>
      </BrandingProvider>
    </BrowserRouter>
    {/* Mobile clip sheet — rendered outside BrowserRouter so it survives route transitions */}
    {mobileClipUrl && (
      <MobileClipSheet
        sourceUrl={mobileClipUrl}
        onClose={() => setMobileClipUrl(null)}
      />
    )}
    </ErrorBoundary>
  )
}
